<template>
  <main class="trip-tnc">
    <section>
      <div class="title">Terms & Conditions for Tour Hosts ("TourHeroes")</div>

      <div>
        <div>Effective Date: {{ effectiveDate }}</div>
        <div>Applicable Trip Dates: {{ tripDates }}</div>
      </div>

      <div>
        Trip Itinerary Link: <br />
        <a :href="productUrl">{{ productUrl }}</a>
      </div>
    </section>

    <section class="timeline">
      <div class="subtitle">Your journey to departure</div>
      <div class="timeline-details">
        <div>
          <Icon1 class="icon" />
          <div class="line"></div>
          <div class="label">Effective launch date</div>
          <div class="date">{{ effectiveDate }}</div>
        </div>
        <div>
          <Icon2 class="icon" />
          <div class="line"></div>
          <div class="label">First booking</div>
          <div class="date">Target: {{ firstBookingDeadline }}</div>
          <div class="tip green">Trip logistics confirmed after first booking</div>
        </div>
        <div>
          <Icon3 class="icon" />
          <div class="line"></div>
          <div class="label">Cancel deadline</div>
          <div class="date">{{ cancellationDeadline }}</div>
          <div class="tip yellow">Cancel risk-free until this date</div>
        </div>
        <div>
          <Icon4 class="icon" />
          <div class="line"></div>
          <div class="label">Applicable trip dates</div>
          <div class="date">{{ tripDatesShort }}</div>
        </div>
      </div>
    </section>

    <section>
      <div class="subtitle">DEFINITIONS</div>

      <div>
        <strong>The TourHero Platform</strong><br />
        The TourHero Platform is a web and mobile application-based platform for like-minded travelers to coordinate,
        meet and go on tours supplied by Operators.
      </div>

      <div>
        <strong>TourHeroes (Tour Hosts)</strong><br />
        TourHeroes are individuals who want to plan a Tour on the TourHero Platform with the help of Operators.
        TourHeroes are responsible for marketing the Tour and getting Travelers to book. They act as the primary liaison
        during the Tour, ensuring a cohesive group experience, while the Operator handles all logistical planning and
        execution.
      </div>

      <div>
        <strong>Operator</strong><br />
        The "Operator" is the entity or entities appointed by the TourHero Platform to run the Tour, responsible for
        managing all on-tour logistics and ensuring the smooth execution of the itinerary.
      </div>

      <div>
        <strong>Travelers</strong><br />
        The "Travelers" are individuals attending the Tour, expected to follow the itinerary and guidelines set by the
        Operator and TourHeroes to ensure a safe and enjoyable experience.
      </div>

      <div>
        <strong>Tour</strong><br />
        The "Tour" is the organized travel experience, including all planned activities, accommodations, and
        transportation, as coordinated by the Operator and marketed by the TourHeroes.
      </div>

      <div>
        <strong>Minimum Participant Requirement and Deadline</strong><br />
        The Tour is dependent on achieving a minimum number of Travelers by the specified cancellation deadline.
      </div>

      <div>
        <strong>Trip Postponement or Cancellation</strong><br />
        If the minimum Travelers requirement is not fulfilled by the cancellation deadline, the Tour may be cancelled or
        postponed.
      </div>

      <div>
        <strong>Refund Policy</strong><br />
        In the event of a Tour postponement or cancellation before the cancellation deadline, Travelers will receive a
        full refund of any payments made.
      </div>

      <div>
        <strong>Free Tour Offer</strong><br />
        TourHeroes are eligible to travel for free once the minimum Travelers requirement is met.
      </div>

      <div>
        <strong>Profit Payment Schedule</strong><br />
        Profit payments to TourHeroes will be made in two installments, provided that the contract sent via Deel is
        signed within the timeframe specified by the TourHero Platform. Any delay in signing may result in a delay in
        payment.
        <br />&bull; 50% of the profit will be paid 14 days before the Tour. <br />
        &bull; The remaining 50% will be paid 14 days after the Tour.
      </div>

      <div>
        <strong>Deduction for Early Bird Coupons</strong><br />
        Any value from early bird coupons used by Travelers and requested or created by TourHeroes will be deducted from
        TourHeroes' profit.
      </div>

      <div>
        <strong>Marketing Responsibilities</strong><br />
        TourHeroes are responsible for actively engaging in marketing efforts for the Tour. By promoting the Tour in any
        public or private capacity, TourHeroes acknowledge and agree to be bound by these Terms & Conditions.
      </div>

      <div>
        <strong>Deposit</strong><br />
        If a Traveler cancels the Tour, TourHeroes are not entitled to any portion of the deposit or payments made to
        the TourHero Platform.
      </div>
    </section>

    <section>
      <div class="subtitle">GENERAL TERMS</div>

      <div>
        <b>1. Minimum Booking Threshold and Cancellation Rights.</b> Each Tour is subject to a minimum of
        {{ minimumBookings }} confirmed Traveler bookings, excluding TourHeroes, by the applicable cancellation deadline
        specified in the itinerary ("Minimum Booking Threshold"). In the event the Minimum Booking Threshold is not met
        by such deadline, the TourHero Platform reserves the right, in its sole discretion, to cancel the Tour without
        liability to any party. Where the Minimum Booking Threshold is met, TourHeroes may nonetheless request
        cancellation of the Tour in writing prior to the cancellation deadline. Failure by TourHeroes to submit a
        written cancellation request by such deadline shall constitute TourHeroes' unconditional confirmation that the
        Tour is to proceed as scheduled, and TourHeroes shall be bound accordingly. The TourHero Platform accepts no
        liability for any losses, costs, or damages arising from a cancellation made in accordance with this provision.
      </div>

      <div>
        <b>2. Financial Terms and Compensation.</b> The financial terms applicable to a Tour are calculated on the basis
        of {{ minimumBookings }} confirmed Traveler bookings, excluding TourHeroes. Subject to the terms of this
        Agreement and any applicable Service Contract, TourHeroes shall receive compensation of
        {{ heroMarkupAmount }} per confirmed Traveler. Payment shall be made in two (2) installments: (i) fifty percent
        (50%) no later than fourteen (14) days prior to the Tour departure date, and (ii) the remaining fifty percent
        (50%) no later than fourteen (14) days following the Tour completion date. The TourHero Platform reserves the
        right to adjust payment dates in extenuating circumstances, including but not limited to Force Majeure events,
        disputes, or unresolved claims. For the avoidance of doubt, compensation is payable only in respect of Travelers
        whose bookings are confirmed and not subject to a pending cancellation or refund request at the time of payment.
      </div>

      <div>
        <b>3. Complimentary Tour.</b> Once the minimum number of Travelers is met, TourHeroes will receive a
        complimentary Tour under the same itinerary (excluding flights and add-ons), in accordance with the terms
        outlined in the table below.
        <ResponsiveTable
          style="margin-top: 8px"
          :headers="['Coupon for', 'Bookings required to receive', 'Note']"
          :rows="[['1x Complimentary Trip', '6', '']]"
        />
      </div>

      <div>
        <b>4. TourHeroes Responsibilities. </b> TourHeroes are responsible for delivering any activities, workshops, or
        experiences they are assigned, as outlined on the itinerary page. They are also expected to collaborate directly
        with the on-ground Operator to ensure smooth execution. TourHero Platform support is available if any assistance
        is needed.
      </div>

      <div>
        <b>5. Accommodations. </b> Unless clearly specified by the TourHero Platform team and in the final itinerary,
        all accommodations listed are to be considered as indicative only and fall under the "or similar" category.
        Operators are only able to block specific room allocations once the first booking has been confirmed, which may
        result in variations in the actual accommodations provided. However, the overall standard and type of
        accommodation will remain consistent with what was initially proposed.
      </div>

      <div>
        <b>6. Early Bird Discounts and Affiliate Programs.</b> Upon request, TourHeroes may activate Early Bird
        discounts and Affiliate programs, wherein a third party receives a commission for promoting the Tour. TourHeroes
        will be responsible for covering the defined discount or agreed commission, deducted directly from their fee
        mentioned above. This amount will be automatically deducted from the corresponding payment.
      </div>

      <div>
        <b>7. Traveler Cancellations.</b> If a Traveler cancels, TourHeroes are not entitled to any of the deposit or
        payment made by that Traveler to the TourHero Platform.
      </div>
    </section>

    <section>
      <div class="subtitle">OTHER TERMS</div>

      <div>
        <strong>Amendments</strong><br />
        These Terms may be updated by the TourHero Platform. Any changes will be communicated in writing.
      </div>
      <div>
        <strong>Severability</strong><br />
        If any part is found invalid, the rest remains enforceable.
      </div>
      <div>
        <strong>Relationship</strong><br />
        These Terms do not create a partnership or employment relationship.
      </div>
      <div>
        <strong>Entire Understanding</strong><br />
        These Terms represent the full understanding of roles and compensation regarding hosting a Tour with the
        TourHero Platform.
      </div>
      <div>
        If you have questions about these Terms & Conditions, please contact
        <a href="mailto:heroes@tourhero.com">heroes@tourhero.com</a>.
      </div>
    </section>

    <section>
      <div class="subtitle">SPECIAL PROVISIONS</div>
      <div>
        This section is dedicated to specific agreements or provisions negotiated between the TourHero Platform and
        TourHeroes. Any terms or conditions outlined below are applicable only for the duration of this contract and
        pertain exclusively to the undersigned TourHeroes. Such provisions are supplementary to the general terms of
        this contract. In the event of any discrepancy or conflict between the general terms of this contract and the
        terms specified in this section, the terms of this "Special Provisions" section shall prevail.
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useTypedItineraryStore } from '@/stores/typed'
import { Product } from '@/models'
import moment from 'moment'
import Icon1 from 'app/assets/images/self-serve/tnc_1.svg?component'
import Icon2 from 'app/assets/images/self-serve/tnc_2.svg?component'
import Icon3 from 'app/assets/images/self-serve/tnc_3.svg?component'
import Icon4 from 'app/assets/images/self-serve/tnc_4.svg?component'
import ResponsiveTable from '@/components/ResponsiveTable.vue'

const store = useTypedItineraryStore<Product>()

store.initialize()

const effectiveDate = computed(() => {
  // Post-publish: use the fixed published date
  if (store.data?.state === 'published' && store.data?.publishedAt) {
    return moment(store.data.publishedAt).format('MMMM D, YYYY')
  }
  // Pre-publish: show today's date
  return moment().format('MMMM D, YYYY')
})

const tripDates = computed(() => {
  const startDate = store.data?.timelines?.tripStartDate
  if (!startDate) return '—'

  const start = moment(startDate)
  const durationDays = store.data?.itinerary?.durationDays ?? 1
  const end = start.clone().add(durationDays - 1, 'days')

  // Format: "October 15-21, 2026" or "October 15 - November 2, 2026" if different months
  if (start.month() === end.month() && start.year() === end.year()) {
    return `${start.format('MMMM D')}-${end.format('D, YYYY')}`
  }
  return `${start.format('MMMM D')} - ${end.format('MMMM D, YYYY')}`
})

const productUrl = computed(() => {
  if (!store.data?.productUrl) return '—'
  return `https://www.tourhero.com${store.data.productUrl}`
})

const firstBookingDeadline = computed(() => {
  const deadline = store.data?.timelines?.firstBookingDeadline
  if (!deadline) return '—'
  return moment(deadline).format('MMM D, YYYY')
})

const cancellationDeadline = computed(() => {
  const deadline = store.data?.timelines?.cancellationDeadline
  if (!deadline) return '—'
  return moment(deadline).format('MMM D, YYYY')
})

const tripDatesShort = computed(() => {
  const startDate = store.data?.timelines?.tripStartDate
  if (!startDate) return '—'

  const start = moment(startDate)
  const durationDays = store.data?.itinerary?.durationDays ?? 1
  const end = start.clone().add(durationDays - 1, 'days')

  // Format: "Apr 17-25, 2026" or "Apr 17 - May 2, 2026" if different months
  if (start.month() === end.month() && start.year() === end.year()) {
    return `${start.format('MMM D')}-${end.format('D, YYYY')}`
  }
  return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`
})

const minimumBookings = computed(() => {
  const minGuestCount = store.data?.minGuestCount
  if (!minGuestCount) return '—'
  return minGuestCount
})

// Hero markup amount in USD (shows $0 if markup is not set/toggled off)
const heroMarkupAmount = computed(() => {
  const markupCents = store.data?.heroMarkupCents ?? 0
  const markupDollars = markupCents / 100
  return `USD ${markupDollars.toFixed(2)}`
})
</script>

<style lang="scss" scoped>
.trip-tnc {
  @include base-font;
  @include flex-column(48px);
  padding-block: 40px 10px;
  max-width: 866px;
  margin-inline: auto;

  @include mobile {
    @include flex-column(32px);
  }

  & > section {
    @include flex-column(16px);
  }

  .title {
    font-size: 24px;
    font-weight: 600;
    line-height: 28px;
  }

  .subtitle {
    font-size: 20px;
    font-weight: 600;
    line-height: 26px; /* 130% */
  }

  a {
    color: $green-300;
  }

  strong {
    font-weight: 600;
  }

  .timeline {
    @include flex-column(24px);
    padding: 24px;
    border-radius: 15px;
    border: 1px solid $grey-50;
    background: white;

    .timeline-details {
      --row-gap: 24px;
      --column-gap: 12px;
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      grid-gap: var(--row-gap) var(--column-gap);

      @include mobile {
        grid-template-columns: 1fr;
      }

      & > div {
        @include flex-column(4px);
        position: relative;

        @include mobile {
          padding: 16px;
          border-radius: 15px;
          border: 1px solid $grey-50;
        }

        &:first-child .line {
          @include desktop {
            left: 50%;
            width: calc(50% + var(--column-gap));
          }

          @include mobile {
            display: none;
          }
        }

        &:last-child .line {
          @include desktop {
            left: 0;
            width: calc(50% + var(--column-gap));
          }
        }
      }

      .line {
        left: 0;
        width: calc(100% + var(--column-gap));
        border-bottom: 4px solid $grey-25;
        position: absolute;
        top: 18px;
        z-index: 1;

        @include mobile {
          height: var(--row-gap);
          border-bottom: none;
          width: 4px;
          background-color: $grey-25;
          top: calc(-1 * var(--row-gap));
          left: 50%;
          transform: translateX(-50%);
        }
      }

      .icon {
        position: relative;
        display: flex;
        width: 40px;
        margin-inline: auto;
        z-index: 2;
      }

      .label,
      .date,
      .tip {
        text-align: center;
      }

      .label {
        font-weight: 600;
      }

      .date {
        color: #6a7282;
      }

      .tip {
        padding: 8px;
        border-radius: 15px;
        font-size: 14px;
        line-height: 16px;

        &.green {
          color: $green-300;
          background: $green-50;
        }

        &.yellow {
          color: $yellow-500;
          background: $yellow-50;
        }
      }
    }
  }
}
</style>
